/**
 * /venues/:venueSlug/tv — the screen on the wall.
 *
 * WHAT IT SHOWS IS DECIDED BY THE ROOM, NOT BY THIS FILE. `phase` comes down
 * on the snapshot, settled server-side, precisely so that two screens in one
 * venue cannot disagree about whether the knockout has started. Nothing here
 * re-derives it.
 *
 *   predraw   fullscreen ad rotation
 *   group     boards, and the standings rotating beside them
 *   knockout  boards, and the bracket beside them — the standings stop
 *   complete  the winner, then back to the ads
 *
 * NOBODY IS STANDING AT THIS SCREEN. Everything it needs, it finds by itself:
 * the tournament, the reconnect after the pub's wifi drops, and the switch
 * from ads to boards when the draw runs. The failure mode of every part of it
 * is something sensible on a wall — never an error message, never a blank.
 *
 * THE BARE URL CARRIES NO TOURNAMENT ID, on purpose. A screen bolted to a pub
 * wall is cast once at the start of the season; asking somebody to find
 * tonight's id first, every week, is asking for the week they get it wrong and
 * the TV spends the night showing last month's results. That screen asks the
 * venue which tournament is active and shows that one.
 *
 * ?tournament_id=N overrides it, and is what the button on a tournament's own
 * admin page opens — an admin who is looking at one night and wants it on a
 * screen should get that night, not whichever one the venue happens to have in
 * play.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import { apiGet } from '../lib/api.js';
import { useVenue } from '../context/AuthContext.jsx';
import { useTournamentRoom } from '../lib/tournamentRoom.js';

import TvBoards    from '../components/tv/TvBoards.jsx';
import TvTicker    from '../components/tv/TvTicker.jsx';
import TvStandings from '../components/tv/TvStandings.jsx';
import TvBracket   from '../components/tv/TvBracket.jsx';
import {
  useVenueAds, useAdInterrupt, sidebarTiming, tvToggles,
  TvFullscreenAds, TvSidebarAd, TvTextScreen,
} from '../components/tv/TvAds.jsx';

/** How often a screen with no tournament yet goes looking for one. */
const HUNT_MS = 60_000;

/** How long the winner holds the screen before the ads come back. */
const WINNER_MS = 2 * 60_000;

/** A charity total is a headline, not an invoice: no pence unless there are. */
function money(amount) {
  const pence = Number(amount) % 1 !== 0;
  return `£${new Intl.NumberFormat('en-GB', {
    minimumFractionDigits: pence ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(Number(amount))}`;
}

/* ------------------------------------------------------------------------- */

/**
 * The venue's tournament in play, or nothing.
 *
 * ONE QUESTION, ASKED ONCE: is a tournament at this venue 'active'? The draw is
 * what makes one active and it refuses to run while another already is (see
 * POST .../draw), so the answer is either one tournament or none — which is
 * what lets this be a lookup rather than a hunt.
 *
 * It used to search active, then registration, then anything completed today,
 * and that ordering was the bug: a venue with an old test night left in
 * registration handed the wall the wrong tournament and nothing short of
 * deleting the others would shift it. There is no ranking to get wrong now.
 *
 * The cost is the window after the final, where the night goes 'completed' and
 * the wall drops to the ads. The room holds the winner on screen for WINNER_MS
 * before that, which covers the trophy; a TV switched on cold ten minutes later
 * shows the pub's own artwork, and that is the right screen for a pub with no
 * darts on.
 *
 * It keeps asking while there is nothing, because the common case is a TV
 * switched on before the draw. A screen that answered "no tournament" once at
 * six o'clock and never asked again would need somebody to walk over and
 * reload it at half past seven, which is exactly when nobody is free.
 */
function useActiveTournament(venueId, enabled) {
  const [tournament, setTournament] = useState(null);

  useEffect(() => {
    if (venueId == null || !enabled) return undefined;

    let cancelled = false;
    let timer     = null;

    const look = async () => {
      const { data } = await apiGet(
        `/api/venues/${venueId}/tournaments?status=active`
      );
      if (cancelled) return;

      const found = (data?.tournaments ?? [])[0] ?? null;

      if (found) {
        // Stop asking: from here the room is the source of truth. A night that
        // finishes while this is on screen arrives as a new snapshot, not as a
        // different tournament.
        setTournament(found);
        return;
      }

      timer = setTimeout(look, HUNT_MS);
    };

    look();

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [venueId, enabled]);

  return tournament;
}

/**
 * The numeric venue id, from the session where there is one and from the slug
 * route where there is not — platform staff hold no user_venue_roles row for a
 * venue they can still administer.
 */
function useVenueId(venueSlug) {
  const contextVenue = useVenue(venueSlug);
  const contextId    = contextVenue?.venue_id ?? null;
  const contextName  = contextVenue?.name ?? null;

  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    if (contextId !== null) {
      setResolved({ id: contextId, name: contextName });
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const { data } = await apiGet(`/api/venues/by-slug/${venueSlug}`);
      if (cancelled || !data?.venue) return;
      setResolved({ id: data.venue.id, name: data.venue.name });
    })();

    return () => { cancelled = true; };
  }, [venueSlug, contextId, contextName]);

  return resolved;
}

/**
 * The venue's public settings, which is where the sidebar interrupt's timing
 * lives.
 *
 * The PUBLIC subset, not /settings — that one is venue_owner and this screen
 * runs as venue_admin, so the private endpoint is not open to it even though
 * somebody is signed in. The three tv_sidebar_* keys were added to the
 * allowlist for exactly this reader.
 *
 * Fetched once, like the ads: a screen that has been up since seven o'clock
 * should not be re-reading settings all evening, and a venue changing these
 * mid-night can reload the TV.
 */
function useVenueSettings(venueId) {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    if (venueId == null) return undefined;

    let cancelled = false;

    (async () => {
      const { data } = await apiGet(`/api/venues/${venueId}/settings/public`);
      // A failed read leaves settings null, and every timing falls back to its
      // default. There is no version of this worth putting on a wall.
      if (!cancelled && data?.settings) setSettings(data.settings);
    })();

    return () => { cancelled = true; };
  }, [venueId]);

  return settings;
}

/* ------------------------------------------------------------------------- */

/**
 * The follow-along code: this night's live view, on a spectator's own phone.
 *
 * ON BY DEFAULT — the one TV toggle that is, because it is useful the day a
 * venue plugs a screen in and nobody has to know it exists to benefit from it.
 *
 * Drawn to a data URL rather than a canvas ref, for the reason Board.jsx gives
 * on its pairing code: it survives a re-render without redrawing and scales
 * with CSS on whatever the screen is. Pure black on white for the same reason
 * too — a themed QR is one some phones will not read across a dim room.
 *
 * It renders NOTHING until the code is drawn. An empty box with a caption
 * under it, on a wall, is worse than no box: people try to scan it.
 */
function FollowAlongQr({ venueSlug, tournamentId }) {
  const [qr, setQr] = useState(null);

  useEffect(() => {
    if (tournamentId == null) return undefined;

    // Absolute, because it is being read by a device that is not on this page
    // and has no origin to resolve against.
    const url = `${window.location.origin}`
      + `/venues/${venueSlug}/tournaments/${tournamentId}/live`;

    let live = true;

    QRCode.toDataURL(url, {
      errorCorrectionLevel: 'M',
      margin: 2,
      width: 512,
      color: { dark: '#000000', light: '#ffffff' },
    })
      // A failure here is not worth a message on a wall. The corner simply
      // stays empty and the darts carry on.
      .then((data) => { if (live) setQr(data); })
      .catch(() => { if (live) setQr(null); });

    return () => { live = false; };
  }, [venueSlug, tournamentId]);

  if (!qr) return null;

  return (
    <div className="hd-tv-qr">
      <img className="hd-tv-qr-code" src={qr} alt="" />
      <span className="hd-tv-qr-caption">Follow along</span>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

function Takeover({ label, headline, venueName }) {
  return (
    <div className="hd-tv-takeover">
      <div className="hd-tv-takeover-label">{label}</div>
      <div className="hd-tv-takeover-headline">{headline}</div>
      <div className="hd-tv-takeover-venue">{venueName}</div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */

export default function Tv() {
  const { venueSlug }  = useParams();
  const [searchParams] = useSearchParams();

  const venue     = useVenueId(venueSlug);
  const venueId   = venue?.id ?? null;
  const venueName = venue?.name ?? null;

  // A pinned id beats the lookup, and stops it running at all — a screen opened
  // for one tournament must not swap itself for another when that one ends.
  // Anything that is not a positive integer is treated as absent rather than
  // passed on: this URL gets typed out and read off phones.
  const pinnedId = useMemo(() => {
    const raw = Number(searchParams.get('tournament_id'));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  }, [searchParams]);

  const active       = useActiveTournament(venueId, pinnedId === null);
  const tournamentId = pinnedId ?? active?.id ?? null;

  const { status, snapshot, completedByBoard } =
    useTournamentRoom(venueId, tournamentId, 'tv');

  const ads      = useVenueAds(venueId);
  const settings = useVenueSettings(venueId);

  // The venue's own numbers, clamped and defaulted where it has set nothing.
  // How often a pub wants its sponsors on screen is a fact about the pub, not
  // about this component, and it is the same answer on every screen in the
  // building — which is why it is a venue setting rather than something typed
  // on the end of a URL, where two TVs in one room would drift apart.
  const { everyMs, forMs, warmupMs } = useMemo(
    () => sidebarTiming(settings), [settings]
  );

  // The venue's display choices, from the same read. Defaults live in
  // tvToggles, not here: the spectator view reads two of these three and the
  // two screens must not disagree about what unset means.
  const toggles = useMemo(() => tvToggles(settings), [settings]);

  const phase   = snapshot?.phase ?? null;
  const playing = phase === 'group' || phase === 'knockout';

  // Nothing to interrupt FOR means no interrupt. Pulling the standings for
  // half a minute to show a screen carrying the venue's own name would be a
  // worse screen than the one it replaced.
  const hasSidebar = ads.sidebar.length > 0 || ads.fallback != null;

  const interrupting = useAdInterrupt({
    enabled: playing && hasSidebar,
    everyMs,
    forMs,
    warmupMs,
  });

  const boards = snapshot?.boards ?? [];
  const groups = snapshot?.standings ?? [];

  // A match row carries a group_id and no group NUMBER; the standings pair a
  // group with its board, which is the join the board cards need.
  const groupByBoard = useMemo(() => {
    const map = {};
    for (const group of groups) {
      if (group.boardNumber != null) map[group.boardNumber] = group.groupNumber;
    }
    return map;
  }, [groups]);

  // The bracket is REST. It moves when a match completes and at no other time,
  // so the count of finished matches is exactly the right refetch key.
  const completedCount = useMemo(
    () => (snapshot?.matches ?? []).filter(
      (m) => m.status === 'completed' || m.status === 'forfeit'
    ).length,
    [snapshot?.matches]
  );

  // Knockout, nothing live anywhere, and something still to play: the gap
  // between rounds. roundLabel is the room's, so the banner and the boards
  // cannot name different rounds.
  //
  // THE PENDING CHECK IS NOT REDUNDANT. When every knockout match is finished,
  // roundLabel falls back to the whole bracket and returns its FIRST round —
  // so a night that has just crowned a champion would otherwise put
  // "Quarter-Finals coming up" across the wall.
  const between = phase === 'knockout'
    && snapshot?.roundLabel
    && !boards.some((b) => b.match?.status === 'live')
    && (snapshot?.matches ?? []).some(
      (m) => m.stage === 'knockout' && m.status === 'pending'
    );

  const winnerName = useMemo(
    () => (snapshot?.matches ?? []).find(
      (m) => m.stage === 'knockout' && m.round === 1 && m.winner_name
    )?.winner_name ?? null,
    [snapshot?.matches]
  );

  // The winner holds the screen, then the room goes back to looking like a pub.
  const [winnerDone, setWinnerDone] = useState(false);

  useEffect(() => {
    if (phase !== 'complete' || !winnerName) return undefined;

    setWinnerDone(false);
    const timer = setTimeout(() => setWinnerDone(true), WINNER_MS);
    return () => clearTimeout(timer);
  }, [phase, winnerName]);

  /* ----------------------------------------------------------------------- */

  // A charity presentation takes the whole screen, whatever else is happening.
  // It is the one thing on here that somebody is standing next to.
  if (snapshot?.totalRaised) {
    return (
      <div className="hd-tv">
        <Takeover
          label={snapshot.totalRaised.label ?? 'Total raised'}
          headline={money(snapshot.totalRaised.amount)}
          venueName={venueName}
        />
      </div>
    );
  }

  if (phase === 'complete' && winnerName && !winnerDone) {
    return (
      <div className="hd-tv">
        <Takeover label="Winner" headline={winnerName} venueName={venueName} />
      </div>
    );
  }

  // No tournament found, nothing drawn yet, the night over, or no snapshot at
  // all: one screen covers all four, because to a room they are the same
  // situation — there are no darts to watch at the moment.
  if (!playing) {
    const note = venueId == null ? 'Loading'
      : tournamentId == null ? 'No tournament tonight'
      : snapshot == null ? 'Connecting to the tournament room'
      : phase === 'complete' ? 'Thanks for coming'
      : null;

    return (
      <div className="hd-tv" data-phase={phase ?? 'predraw'}>
        <TvFullscreenAds ads={ads} venueName={venueName} note={note} />
      </div>
    );
  }

  return (
    <div className="hd-tv" data-phase={phase}>
      <header className="hd-tv-topbar">
        <span className="hd-tv-venue">{venueName}</span>

        <span className="hd-tv-phase">
          {phase === 'knockout' ? snapshot.roundLabel ?? 'Knockout' : 'Group stage'}
        </span>

        {/* Small, and only when it is bad news. A wall does not need telling
            that things are working. */}
        {status !== 'connected' && (
          <span className="hd-tv-offline">Reconnecting</span>
        )}

        {/* Only while there are darts to follow. A code that opens an empty
            page is a code that teaches the room not to bother scanning it. */}
        {toggles.followQr && tournamentId != null && (
          <FollowAlongQr venueSlug={venueSlug} tournamentId={tournamentId} />
        )}
      </header>

      {between && (
        <div className="hd-tv-banner">{snapshot.roundLabel} coming up</div>
      )}

      <main className="hd-tv-main">
        {boards.length > 0 ? (
          <TvBoards
            boards={boards}
            completedByBoard={completedByBoard}
            groupByBoard={groupByBoard}
            showLiveAverages={toggles.liveAverages}
          />
        ) : (
          <TvTextScreen venueName={venueName} note="Waiting for the boards" />
        )}

        {/* The panel is one column with two occupants. The standings stay
            MOUNTED through an interrupt with their timer stopped, so the
            rotation resumes at the group it was on rather than starting again
            at group one every ten minutes. */}
        <aside className="hd-tv-panel">
          <div className="hd-tv-panel-body" hidden={interrupting}>
            {phase === 'knockout' ? (
              <TvBracket
                venueId={venueId}
                tournamentId={tournamentId}
                refreshKey={completedCount}
              />
            ) : (
              <TvStandings groups={groups} enabled={!interrupting} />
            )}
          </div>

          {interrupting && (
            <TvSidebarAd ads={ads} venueName={venueName} forMs={forMs} />
          )}
        </aside>
      </main>

      {/* Along the bottom, under both columns: every board's next fixture and
          its marker. Fed from the same snapshot the grid above it draws — see
          TvTicker. It takes itself off screen when nothing is queued. */}
      <TvTicker boards={boards} />
    </div>
  );
}
